import React, { useEffect } from "react";
import SignaturesTab from "../../components/SignatureTab";
import { Permit } from "../../models/Permit";
import { AppState } from "../../store";
import { connect } from "react-redux";
import { submitPermitAuthorisation } from "./actions";
import { User } from "../../models/User";
import { Guid } from "guid-typescript";
import PermitFlagService, { GreenFlag } from "../../PermitFlagService";
import { RawFile } from "../../models/RawFile";
import { PermitAuthorisationSignature } from "../../models/PermitAuthorisationSignature";
import { Role } from "../../models/Role";
import PermissionService from "../../PermissionService";
import { ISSUE_PERMIT, READY_TO_ISSUE } from "../../constants";
import { updatePermitLastAccess } from "../PermitContainer/thunks";
import { GetPositionOptions, LocationErrorCode } from "../../LocationService";
import LocationService from "../../LocationService";

interface SignaturesTabContainerProps {
    permit: Permit | null;
    permitGeofenceCheckResult: {
        isWithinGeofence: boolean;
        error: string | null;
        errorCode: LocationErrorCode | null;
    } | null;
    clientRoles: Role[];
    currentUser: User | null;
    projectUsers: User[];
    submitPermitAuthorisation: typeof submitPermitAuthorisation;
    updatePermitLastAccess: typeof updatePermitLastAccess;
    isReadOnly: boolean;
}

const SignaturesTabContainer: React.FC<SignaturesTabContainerProps> = ({
    permit,
    permitGeofenceCheckResult,
    clientRoles,
    currentUser,
    projectUsers,
    submitPermitAuthorisation,
    updatePermitLastAccess,
    isReadOnly,
}) => {
    const requestUpdatePermitLastAccess = (options?: GetPositionOptions) => {
        if (permit && currentUser) {
            updatePermitLastAccess(permit, currentUser.id, options);
        }
    };

    useEffect(() => {
        requestUpdatePermitLastAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permit?.id, currentUser?.id]);

    const handleRefreshButtonClicked = async () => {
        if (permitGeofenceCheckResult?.errorCode === LocationErrorCode.PERMISSION_DENIED) {
            await LocationService.requestPermissions().catch(() => {});
        }
        requestUpdatePermitLastAccess({ maximumAge: 0 });
    };

    const generateFileKey = (): string => {
        return "file-" + Guid.create().toString();
    };

    const handlePermitAssignmentSignatureSubmitted = async (
        signature: PermitAuthorisationSignature,
        signatureDataUrl: string
    ) => {
        const fileKey = generateFileKey();

        const coordinates =
            permit?.lastAccess?.latitude && permit?.lastAccess?.longitude
                ? { latitude: permit.lastAccess.latitude, longitude: permit.lastAccess.longitude }
                : undefined;

        currentUser &&
            submitPermitAuthorisation(
                {
                    ...signature,
                    dateSigned: new Date(),
                    signatureFileKey: fileKey,
                    coordinates: coordinates,
                } as PermitAuthorisationSignature,
                { fileName: fileKey, contentType: "image/png", dataUrl: signatureDataUrl } as RawFile,
                clientRoles,
                currentUser.id
            );
    };

    const assigneesWhoShouldSign = permit?.permitAssignments?.filter(
        (a) => clientRoles.find((c) => c.id === a.roleId)?.permitRoleSettings.shouldAuthorizePermit
    );

    if (!permit || !currentUser) return null;
    const ableToIssuePermit = PermissionService.userHasPermission(
        currentUser,
        ISSUE_PERMIT,
        clientRoles,
        permit.project.id
    );

    const isPermitFlagForAuthorisationGreen = PermitFlagService.getPermitFlagForAuthorisation(permit) === GreenFlag;
    const isPermitStatusReadyToIssue = permit.status === READY_TO_ISSUE;
    const abilityToSign = !isPermitStatusReadyToIssue
        ? true
        : isPermitFlagForAuthorisationGreen &&
          (!((permit?.geofencing?.status ?? "disabled") === "enforced") ||
              (permitGeofenceCheckResult?.isWithinGeofence ?? false));

    return (
        <SignaturesTab
            permitId={permit.id}
            assigneesWhoShouldSign={assigneesWhoShouldSign}
            signatures={permit.signatures}
            clientRoles={clientRoles}
            projectUsers={projectUsers}
            onPermitAssignmentSignatureSubmitted={handlePermitAssignmentSignatureSubmitted}
            onRefreshButtonClicked={handleRefreshButtonClicked}
            currentUser={currentUser}
            abilityToSign={abilityToSign}
            isReadOnly={!ableToIssuePermit}
            shouldShowCompletePreviousTabsError={!isPermitFlagForAuthorisationGreen}
            shouldShowGeofencingError={
                isPermitStatusReadyToIssue &&
                (permit?.geofencing?.status ?? "disabled") !== "disabled" &&
                !permitGeofenceCheckResult?.isWithinGeofence
            }
            geofencingError={permitGeofenceCheckResult?.error ?? null}
        />
    );
};

function mapStateToProps(state: AppState) {
    return {
        permit: state.permitPage.permit,
        permitGeofenceCheckResult: state.permitPage.permitGeofenceCheckResult,
        currentUser: state.user.currentUser,
        clientRoles: state.myPermits.clientRoles,
        projectUsers: state.permitAssignments.projectUsers,
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        submitPermitAuthorisation: (
            authorisation: PermitAuthorisationSignature,
            signatureFile: RawFile,
            clientRoles: Role[],
            loggedInUserId: string
        ) => dispatch(submitPermitAuthorisation(authorisation, signatureFile, clientRoles, loggedInUserId)),
        updatePermitLastAccess: (permit: Permit, userId: string, options?: GetPositionOptions) =>
            dispatch(updatePermitLastAccess(permit, userId, options)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignaturesTabContainer);
